import type { MutateInteropSupportedResult } from '@confluence/apollo-relay-interop/entry-points/mutate.ts';
import { mutateInterop } from '@confluence/apollo-relay-interop/entry-points/mutate.ts';

import type {
	DeleteDraftMutation$data,
	DeleteDraftMutation$variables,
} from './__generated__/DeleteDraftMutation.graphql.ts';
import DeleteDraftMutation from './__generated__/DeleteDraftMutation.graphql.ts';

/**
 * A draft should be deleted if:
 *
 * 1. Is unpublished draft
 * 2. Title is blank
 * 3. Content is blank
 * 4. There are no other collaborators
 * 5. No restrictions are applied
 * 6. No cover photo added
 * 7. No title emoji selected
 * 8. No labels
 */
export const blankDraftInfo = ({
	isUnpublishedDraft,
	isSingleUserSession,
	hasTitle,
	hasBody,
	hasEmoji,
	hasCoverPicture,
	hasContentState,
	hasRestrictions,
	hasLabels,
}: {
	isSingleUserSession: boolean;
	isUnpublishedDraft: boolean;
	hasTitle: boolean;
	hasBody: boolean;
	hasEmoji: boolean;
	hasContentState: boolean;
	hasCoverPicture: boolean;
	hasRestrictions: boolean;
	hasLabels: boolean;
}): {
	isBlank: boolean;
	isUnpublishedDraft: boolean;
	isSingleUserSession: boolean;
	hasTitle: boolean;
	hasBody: boolean;
	hasEmoji: boolean;
	hasCoverPicture: boolean;
	hasContentState: boolean;
	hasRestrictions: boolean;
	hasLabels: boolean;
} => {
	const isBlank =
		isSingleUserSession &&
		isUnpublishedDraft &&
		!hasTitle &&
		!hasBody &&
		!hasEmoji &&
		!hasEmoji &&
		!hasCoverPicture &&
		!hasContentState &&
		!hasRestrictions &&
		!hasLabels;

	return {
		isBlank,
		isUnpublishedDraft,
		isSingleUserSession,
		hasTitle,
		hasBody,
		hasEmoji,
		hasCoverPicture,
		hasContentState,
		hasRestrictions,
		hasLabels,
	};
};

/**
 * Delete draft
 *
 */
type DeleteDraftProps = {
	contentId: string;
	deleteOnlyIfBlank: boolean;
};
export const deleteDraft = ({
	contentId,
	deleteOnlyIfBlank,
}: DeleteDraftProps): Promise<MutateInteropSupportedResult<DeleteDraftMutation$data>> =>
	mutateInterop<DeleteDraftMutation$data, DeleteDraftMutation$variables>({
		mutation: DeleteDraftMutation,
		variables: {
			contentId,
			action: deleteOnlyIfBlank ? 'DELETE_DRAFT_IF_BLANK' : 'DELETE_DRAFT',
		},
	});
